import React, { useState } from "react";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import { AdminInbox } from "@/components/AdminInbox";
import { PracticeStats } from "@/components/PracticeStats";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Activity, Timer, Library, User, LogOut, Crown, CreditCard, Users, Gift, Flame, KeyRound, Inbox, BarChart3 } from "lucide-react";
import { toast } from "sonner";

export default function Header({ onOpenAuth, onOpenMetronome, onOpenLibrary, onOpenPricing, onOpenTeam, onOpenReferral, libraryCount }) {
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);

  const openBilling = async () => {
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error("Could not open billing portal");
    }
  };

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 h-20 backdrop-blur-xl bg-[#0b0c10]/85 border-b border-white/10"
    >
      <div className="h-full max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-end gap-[3px] h-7">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className="wave-bar w-[3px] rounded-full bg-[#00f0ff]"
                style={{ height: "100%", animationDelay: `${i * 0.12}s` }}
              />
            ))}
          </div>
          <div className="leading-none">
            <div className="font-heading text-2xl font-extrabold uppercase tracking-tight">
              Beat<span className="cyan-text">Flow</span>
            </div>
            <div className="text-[10px] tracking-[0.3em] text-slate-500 uppercase font-tab">
              AI Drum Tabs
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenMetronome}
            className="text-slate-300 hover:text-[#00f0ff] hover:bg-white/5"
            data-testid="metronome-toggle-button"
          >
            <Timer className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Metronome</span>
          </Button>

          {user && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenLibrary}
              className="relative text-slate-300 hover:text-[#ffab00] hover:bg-white/5"
              data-testid="library-toggle-button"
            >
              <Library className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Library</span>
              {libraryCount > 0 && (
                <Badge className="ml-2 bg-[#ffab00] text-black px-1.5 py-0 text-[10px]">
                  {libraryCount}
                </Badge>
              )}
            </Button>
          )}

          {user && user.streak >= 2 && (
            <Badge
              className="bg-[#ff6b00]/15 text-[#ff8c3a] border-0 font-tab flex items-center gap-1 px-2"
              title={`${user.streak}-day practice streak`}
              data-testid="streak-badge"
            >
              <Flame className="w-3.5 h-3.5" />
              {user.streak}
            </Badge>
          )}

          {user && !user.is_pro && (
            <Button
              onClick={onOpenPricing}
              size="sm"
              className="bg-[#ffab00] text-black hover:bg-[#ffc233] font-semibold"
              data-testid="header-upgrade-button"
            >
              <Crown className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Upgrade</span>
            </Button>
          )}
          {user && user.is_pro && (
            <Badge className="bg-[#00f0ff]/15 text-[#00f0ff] border-0 font-tab hidden sm:flex items-center gap-1" data-testid="plan-badge">
              <Crown className="w-3 h-3" />
              {user.plan === "lifetime" ? "LIFETIME" : "PRO"}
            </Badge>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/15 bg-white/5 hover:bg-white/10"
                  data-testid="user-profile-menu"
                >
                  <User className="w-4 h-4 sm:mr-2 cyan-text" />
                  <span className="hidden sm:inline max-w-[100px] truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#12161f] border-white/10">
                <DropdownMenuLabel className="text-slate-400 text-xs">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                {user.is_team_owner && (
                  <DropdownMenuItem
                    onClick={onOpenTeam}
                    className="focus:bg-white/5 cursor-pointer"
                    data-testid="menu-team-button"
                  >
                    <Users className="w-4 h-4 mr-2 amber-text" />
                    My Team
                  </DropdownMenuItem>
                )}
                {user.has_billing && (
                  <DropdownMenuItem
                    onClick={openBilling}
                    className="focus:bg-white/5 cursor-pointer"
                    data-testid="menu-billing-button"
                  >
                    <CreditCard className="w-4 h-4 mr-2 cyan-text" />
                    Manage billing
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={onOpenReferral}
                  className="focus:bg-white/5 cursor-pointer"
                  data-testid="menu-referral-button"
                >
                  <Gift className="w-4 h-4 mr-2 cyan-text" />
                  Refer &amp; earn
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem
                    onClick={() => setInboxOpen(true)}
                    className="focus:bg-white/5 cursor-pointer"
                    data-testid="menu-admin-inbox-button"
                  >
                    <Inbox className="w-4 h-4 mr-2 amber-text" />
                    Feedback inbox
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => setStatsOpen(true)}
                  className="focus:bg-white/5 cursor-pointer"
                  data-testid="menu-practice-stats-button"
                >
                  <BarChart3 className="w-4 h-4 mr-2 cyan-text" />
                  My practice
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setPwOpen(true)}
                  className="focus:bg-white/5 cursor-pointer"
                  data-testid="menu-change-password-button"
                >
                  <KeyRound className="w-4 h-4 mr-2 cyan-text" />
                  Change password
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    toast.success("Logged out");
                  }}
                  className="text-[#FF3366] focus:text-[#FF3366] focus:bg-white/5 cursor-pointer"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={onOpenAuth}
              size="sm"
              className="bg-[#00f0ff] text-black hover:bg-[#33f4ff] font-semibold"
              data-testid="auth-open-button"
            >
              <Activity className="w-4 h-4 mr-2" />
              Login
            </Button>
          )}
        </div>
      </div>
      <ChangePasswordModal open={pwOpen} onOpenChange={setPwOpen} />
      {user && <PracticeStats open={statsOpen} onOpenChange={setStatsOpen} />}
      {user?.role === "admin" && <AdminInbox open={inboxOpen} onOpenChange={setInboxOpen} />}
    </header>
  );
}
